import React from 'react';
import { Link } from 'react-router-dom';
import { useFilterPresets } from '../../presets-management/useData';
import { useTheme } from '../../../contexts/ThemeContext';

export interface FilterPresetsWidgetProps {
  limit?: number;
  embedded?: boolean;
  settings?: { limit?: number };
}

const FilterPresetsWidget: React.FC<FilterPresetsWidgetProps> = ({
  limit: limitProp,
  embedded,
  settings,
}) => {
  const limit = settings?.limit ?? limitProp ?? 5;
  const { filterPresets, isLoading } = useFilterPresets(undefined, true);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const displayPresets = (filterPresets ?? []).slice(0, limit);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-10 rounded-lg animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {displayPresets.length === 0 ? (
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          No filter presets yet. Create presets from any list page to quick‑filter data.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {displayPresets.map((preset) => (
            <li key={preset.id}>
              <Link
                to={`/presets-management?preset=${preset.id}`}
                className={`block truncate rounded-lg px-3 py-2 text-sm transition-colors ${
                  isDark
                    ? 'text-gray-200 hover:bg-gray-700'
                    : 'text-gray-800 hover:bg-gray-100'
                }`}
              >
                <span className="font-medium">{preset.name}</span>
                {preset.moduleName && (
                  <span className={`ml-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    · {preset.moduleName}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
      <Link
        to="/presets-management"
        className={`mt-2 inline-block text-sm font-medium ${
          isDark ? 'text-teal-400 hover:text-teal-300' : 'text-teal-600 hover:text-teal-700'
        }`}
      >
        {displayPresets.length === 0 ? 'Manage filter presets' : 'View all presets'}
      </Link>
    </div>
  );
};

export default FilterPresetsWidget;
